import { ChartDataset, ScatterDataPoint } from 'chart.js';
import { basePalette } from '../../../../../../../design-system';
import { ChargePointLogs } from '../../../../../../../utils/ChargePointLogs';
import {
  MeterValueMeasurand,
  Phase,
  SampledValueWithTimestamp,
  Timestamp,
} from '../../../../../../../utils/meterValues';

type ChartPhase = Exclude<Phase, 'L1-N' | 'L2-N' | 'L3-N'>;
type LocationLabel = 'EV' | 'House';
type MessagesByPhase = Record<ChartPhase, SampledValueWithTimestamp[]>;
type MeteringMeasurand = Exclude<MeterValueMeasurand, 'Energy.Active.Export.Register' | 'Frequency'>;
type LineColour = string;

type ColoursByLocation = Record<LocationLabel, Partial<Record<ChartPhase, LineColour>>>;

const StandardColours: ColoursByLocation = {
  EV: {
    L1: basePalette.red.light,
    L2: basePalette.amber.light,
    L3: basePalette.blue.light,
    All: basePalette.purple.light,
  },
  House: {
    L1: basePalette.red.darkest,
    L2: basePalette.amber.dark,
    L3: basePalette.blue.darkest,
    All: basePalette.purple.darkest,
  },
};

const MeteringChartColourPalette: Partial<Record<MeteringMeasurand, Partial<ColoursByLocation>>> = {
  'Current.Import': StandardColours,
  'Current.Offered': { EV: { All: basePalette.green.main } },
  'Energy.Active.Import.Register': StandardColours,
  'Power.Active.Import': {
    EV: { ...StandardColours.EV, All: basePalette.green.main },
    House: { ...StandardColours.House, All: basePalette.purple.main },
  },
  Voltage: { EV: { ...StandardColours.EV, All: basePalette.green.main } },
};

export class MeteringChartModel {
  private static readonly EmptyMeterValues: MessagesByPhase = {
    All: [],
    L1: [],
    L2: [],
    L3: [],
  };

  static readonly MetricTitle: Record<MeteringMeasurand, string> = {
    'Current.Import': 'Current (import)',
    'Current.Offered': 'Current (offered)',
    Voltage: 'Voltage',
    'Power.Active.Import': 'Power',
    'Energy.Active.Import.Register': 'Energy',
    Temperature: 'Temperature',
  };

  private static readonly PhaseSuffix: Record<ChartPhase, string> = {
    L1: ' (ϕ1)',
    L2: ' (ϕ2)',
    L3: ' (ϕ3)',
    All: '',
  };

  private readonly logs: ChargePointLogs;

  private readonly measurand: MeteringMeasurand;

  constructor(args: { logs: ChargePointLogs; measurand: MeteringMeasurand }) {
    const { logs, measurand } = args;
    this.logs = logs;
    this.measurand = measurand;
  }

  toChartData(): ChartDataset<'line'>[] {
    if (this.measurand === 'Temperature') {
      return this.toTemperatureChartData();
    }

    const messagesByLocation = this.splitLogsByLocation();

    return Object.keys(messagesByLocation).reduce<ChartDataset<'line'>[]>((acc, key) => {
      const locationLabel = key as LocationLabel;

      const dataset = this.toChartDataByPhase({ dataByPhase: messagesByLocation[locationLabel], locationLabel });

      return [...acc, ...dataset];
    }, []);
  }

  private static toAxisValues(messages: SampledValueWithTimestamp[]): ScatterDataPoint[] {
    return messages.map((data) => ({ x: new Date(data.timestamp).getTime(), y: parseFloat(data.value) }));
  }

  private static filterByPhase(args: { dataByPhase: MessagesByPhase }): Partial<MessagesByPhase> {
    const { dataByPhase } = args;
    const { All, L1, L2, L3 } = dataByPhase;

    const isThreePhase = L2.length > 0 && L3.length > 0;

    if (isThreePhase) {
      return { L1, L2, L3 };
    }

    if (All.length > 0) {
      return { All };
    }

    if (L1.length > 0) {
      return { All: L1 };
    }

    return {};
  }

  private toDatasetByLocationAndPhase(args: {
    locationLabel: LocationLabel;
    data: SampledValueWithTimestamp[];
    phase: ChartPhase;
  }): ChartDataset<'line'> {
    const { data, locationLabel, phase } = args;
    const measurandColourPalette = MeteringChartColourPalette[this.measurand];
    return {
      borderColor: measurandColourPalette?.[locationLabel]?.[phase] ?? basePalette.green.main,
      borderDash: locationLabel === 'House' ? [2] : [],
      borderWidth: 1,
      data: MeteringChartModel.toAxisValues(data),
      label: this.getDatasetLabel({ locationLabel, phase }),
    };
  }

  private splitLogsByLocation(): Record<LocationLabel, MessagesByPhase> {
    const meterValueMessages = this.logs.meterValuesForMeasurand({ measurand: this.measurand });
    return meterValueMessages.reduce<Record<LocationLabel, MessagesByPhase>>(
      (acc, meterValueMessage) => {
        const phase = meterValueMessage.phase ?? 'All';
        const location = meterValueMessage.location === 'Inlet' ? 'House' : 'EV';
        if (phase === 'L1-N' || phase === 'L2-N' || phase === 'L3-N') {
          return acc;
        }
        return { ...acc, [location]: { ...acc[location], [phase]: [...acc[location][phase], meterValueMessage] } };
      },
      { EV: MeteringChartModel.EmptyMeterValues, House: MeteringChartModel.EmptyMeterValues },
    );
  }

  private toChartDataByPhase(args: {
    dataByPhase: MessagesByPhase;
    locationLabel: LocationLabel;
  }): ChartDataset<'line'>[] {
    const { dataByPhase, locationLabel } = args;

    const perPhaseData = MeteringChartModel.filterByPhase({ dataByPhase });
    const phases = Object.keys(perPhaseData);

    const startingDataset = phases.length > 1 ? this.toSummedPowerData({ perPhaseData, locationLabel }) : [];

    return phases.reduce<ChartDataset<'line'>[]>((acc, k) => {
      const phase = k as ChartPhase;
      const data = perPhaseData[phase];
      if (!data) {
        return acc;
      }
      return [...acc, this.toDatasetByLocationAndPhase({ locationLabel, phase, data })];
    }, startingDataset);
  }

  private static sumDataAtTimestamp(args: { dataByPhase: Partial<MessagesByPhase> }): SampledValueWithTimestamp[] {
    const { dataByPhase } = args;

    const atTimestamp = Object.keys(dataByPhase).reduce<Record<Timestamp, SampledValueWithTimestamp>>((acc, k) => {
      const phase = k as ChartPhase;
      const data = dataByPhase[phase] ?? [];
      return data.reduce<Record<Timestamp, SampledValueWithTimestamp>>((values, meterValue) => {
        const valueForTimestamp = this.addValueForTimestamp({
          newMeterValue: meterValue,
          previousMeterValue: acc[meterValue.timestamp],
        });
        return { ...values, ...valueForTimestamp };
      }, acc);
    }, {});

    return Object.values(atTimestamp);
  }

  private static addValueForTimestamp(args: {
    newMeterValue: SampledValueWithTimestamp;
    previousMeterValue: SampledValueWithTimestamp | undefined;
  }): Record<Timestamp, SampledValueWithTimestamp> {
    const { newMeterValue, previousMeterValue } = args;
    const { timestamp } = newMeterValue;

    if (!previousMeterValue) {
      return { [timestamp]: newMeterValue };
    }

    const sumForTimestamp = parseFloat(previousMeterValue.value) + parseFloat(newMeterValue.value);
    return { [timestamp]: { ...previousMeterValue, value: sumForTimestamp.toString() } };
  }

  private toSummedPowerData(args: {
    perPhaseData: Partial<MessagesByPhase>;
    locationLabel: LocationLabel;
  }): ChartDataset<'line'>[] {
    const { locationLabel, perPhaseData } = args;
    if (this.measurand !== 'Power.Active.Import') {
      return [];
    }
    const valuesPerTimestamp = MeteringChartModel.sumDataAtTimestamp({
      dataByPhase: perPhaseData,
    });
    if (valuesPerTimestamp.length === 0) {
      return [];
    }
    return [this.toDatasetByLocationAndPhase({ locationLabel, data: valuesPerTimestamp, phase: 'All' })];
  }

  private toTemperatureChartData(): ChartDataset<'line'>[] {
    const data = this.logs.meterValuesForMeasurand({ measurand: 'Temperature' });
    if (data.length === 0) {
      return [];
    }
    return [
      {
        borderColor: basePalette.green.main,
        borderWidth: 1,
        data: MeteringChartModel.toAxisValues(data),
        label: MeteringChartModel.MetricTitle.Temperature,
      },
    ];
  }

  private getDatasetLabel(args: { locationLabel: LocationLabel; phase: ChartPhase }) {
    const { locationLabel, phase } = args;

    const measurandLabel = MeteringChartModel.MetricTitle[this.measurand];
    const phaseLabel = phase && MeteringChartModel.PhaseSuffix[phase];

    return phaseLabel ? `${locationLabel} - ${measurandLabel}${phaseLabel}` : `${locationLabel} - ${measurandLabel}`;
  }
}
